// src/hooks/usePermissions.js
import { useAuth } from './useAuth';

const ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  EXPORT: 'export',
  IMPORT: 'import',
  APPROVE: 'approve',
  ASSIGN: 'assign',
  UNASSIGN: 'unassign',
  SEND: 'send',
  VIEW_REPORTS: 'view_reports',
  WRITE_CLASS_TEACHER_COMMENT: 'write_class_teacher_comment',
  WRITE_HEADTEACHER_COMMENT: 'write_headteacher_comment',
  MANAGE_ALL: 'manage_all',
};

const RESOURCES = {
  DASHBOARD: 'dashboard',
  STUDENTS: 'students',
  SCHOOL: 'school',
  CLEARANCE: 'clearance',
  FEES: {
    MANAGEMENT: 'fees/management',
    TYPES: 'fees/types',
    GROUPS: 'fees/groups',
    ALLOCATIONS: 'fees/allocations',
    PAYMENTS: 'fees/payments',
    REPORTS: 'fees/reports',
  },
  ACADEMIC: {
    CLASSES: 'academic/classes',
    LEVELS: 'academic/levels',
    TERMS: 'academic/terms',
    STREAMS: 'academic/streams',
  },
  ACCOUNTING: {
    ACCOUNTS: 'accounting/accounts',
    TRANSACTIONS: 'accounting/transactions',
    VOUCHERS: 'accounting/vouchers',
  },
  COMMUNICATIONS: {
    SMS: 'communications/sms',
    EMAIL: 'communications/email',
    REMINDERS: 'communications/reminders',
  },
  ASSESSMENTS: {
    TYPES: 'report-card/assessment-types',
    CONFIGURATIONS: 'report-card/assessment-configurations',
    MARKS: 'report-card/student-marks',
    REPORTS: 'report-card/assessment-reports',
  },
  REPORT_CARD: {
    GENERAL_COMMENTS: 'report-card/general-comments',
    SUBJECT_COMMENTS: 'report-card/subject-comments',
    CONDUCT: 'report-card/conduct',
    COMMENT_TEMPLATES: 'report-card/comment-templates',
    GRADING: 'report-card/grading',
    TEACHERS: 'report-card/teachers',
    SUBJECTS: 'report-card/subjects',
    SUBJECT_PAPERS: 'report-card/subject-papers',
    CLASS_STREAM_PAPERS: 'report-card/class-stream-papers',
    CLASS_TEACHER_ALLOCATIONS: 'report-card/class-teacher-allocations',
    SUBJECT_TEACHER_ALLOCATIONS: 'report-card/subject-teacher-allocations',
    REPORT_CARDS: 'report-card/report-cards',
    TEMPLATES: 'report-card/templates',
    PRIMARY_PREFERENCES: 'report-card/primary-preferences',
  },
  USERS: 'users',
  ROLES: 'roles',
  PERMISSIONS: 'permissions',
};

export const RESOURCE_LABELS = {
  'dashboard': 'Dashboard',
  'students': 'Students',
  'school': 'School Settings',
  'clearance': 'Clearance',
  'users': 'Users',
  'roles': 'Roles',
  'permissions': 'Permissions',
  'fees/management': 'Fee Management',
  'fees/types': 'Fee Types',
  'fees/groups': 'Fee Groups',
  'fees/allocations': 'Fee Allocations',
  'fees/payments': 'Fee Payments',
  'fees/reports': 'Fee Reports',
  'academic/classes': 'Academic Classes',
  'academic/levels': 'Academic Levels',
  'academic/terms': 'Academic Terms',
  'academic/streams': 'Academic Streams',
  'accounting/accounts': 'Accounts',
  'accounting/transactions': 'Transactions',
  'accounting/vouchers': 'Voucher Heads',
  'communications/sms': 'SMS',
  'communications/email': 'Email',
  'communications/reminders': 'Reminders',
  'report-card/assessment-types': 'Assessment Types',
  'report-card/assessment-configurations': 'Assessment Configurations',
  'report-card/student-marks': 'Student Marks',
  'report-card/assessment-reports': 'Assessment Reports',
  'report-card/general-comments': 'General Comments',
  'report-card/subject-comments': 'Subject Comments',
  'report-card/conduct': 'Student Conduct',
  'report-card/comment-templates': 'Comment Templates',
  'report-card/grading': 'Grading Scales',
  'report-card/teachers': 'Teachers',
  'report-card/subjects': 'Subjects',
  'report-card/subject-papers': 'Subject Papers',
  'report-card/class-stream-papers': 'Class Stream Papers',
  'report-card/class-teacher-allocations': 'Class Teacher Allocations',
  'report-card/subject-teacher-allocations': 'Subject Teacher Allocations',
  'report-card/report-cards': 'Report Cards',
  'report-card/templates': 'Report Card Templates',
  'report-card/primary-preferences': 'Primary Preferences',
};

export const getResourceLabel = (name) => RESOURCE_LABELS[name] || name;

export const usePermissions = () => {
  const { user, permissions, loading, hasPermission: authHasPermission } = useAuth();

  const hasPermission = (resource, action) => {
    return authHasPermission(resource, action);
  };

  const canRead = (resource) => hasPermission(resource, ACTIONS.READ);
  const canCreate = (resource) => hasPermission(resource, ACTIONS.CREATE);
  const canUpdate = (resource) => hasPermission(resource, ACTIONS.UPDATE);
  const canDelete = (resource) => hasPermission(resource, ACTIONS.DELETE);
  const canExport = (resource) => hasPermission(resource, ACTIONS.EXPORT);
  const canImport = (resource) => hasPermission(resource, ACTIONS.IMPORT);
  const canSend = (resource) => hasPermission(resource, ACTIONS.SEND);
  const canAssign = (resource) => hasPermission(resource, ACTIONS.ASSIGN);
  const canApprove = (resource) => hasPermission(resource, ACTIONS.APPROVE);
  const canUnassign = (resource) => hasPermission(resource, ACTIONS.UNASSIGN);
  const canWriteClassTeacherComment = (resource) => hasPermission(resource, ACTIONS.WRITE_CLASS_TEACHER_COMMENT);
  const canWriteHeadteacherComment = (resource) => hasPermission(resource, ACTIONS.WRITE_HEADTEACHER_COMMENT);

  return {
    user,
    permissions,
    loading,
    hasPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canExport,
    canImport,
    canSend,
    canAssign,
    canApprove,
    canUnassign,
    canWriteClassTeacherComment,
    canWriteHeadteacherComment,
    RESOURCES,
    ACTIONS
  };
};

export { RESOURCES, ACTIONS };